import { useEffect, useRef, useState } from 'react';
import { defineMaterial, type MaterialComponentProps } from '@h5/schema';
import { AudioPropsSchema, type AudioProps } from './schema';

export { AudioPropsSchema, type AudioProps };

/**
 * 音频物料：圆形音符图标，点击切换播放，播放中图标旋转。
 * 编辑态不创建 <audio>，不自动播放。
 * 微信里 autoplay 通常被拦截，运行时应在首次触摸后调用 play；这里只尽力 play 并忽略失败。
 */
function AudioView({ props, ctx }: MaterialComponentProps<AudioProps>) {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const isView = ctx.mode === 'view';

  useEffect(() => {
    if (!isView || !props.autoplay || !ref.current) return;
    ref.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [isView, props.autoplay, props.src]);

  const toggle = () => {
    if (!isView || !ref.current) return;
    if (ref.current.paused) {
      ref.current.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      ref.current.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      onClick={toggle}
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        background: props.bgColor,
        color: props.iconColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '55%',
        cursor: 'pointer',
        userSelect: 'none',
        boxSizing: 'border-box',
        animation: playing ? 'h5-audio-spin 3s linear infinite' : undefined,
      }}
    >
      <style>{'@keyframes h5-audio-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}'}</style>
      <span style={{ lineHeight: 1 }}>♫</span>
      {isView && props.src ? (
        <audio ref={ref} src={ctx.assets.url(props.src)} loop={props.loop} preload="auto" onEnded={() => setPlaying(false)} />
      ) : null}
    </div>
  );
}

export const audioMaterial = defineMaterial<AudioProps>({
  id: 'audio',
  version: 1,
  name: '音频',
  category: 'media',
  icon: '♫',
  defaultSize: { w: 36, h: 36 },
  defaultProps: AudioPropsSchema.parse({}),
  propsSchema: AudioPropsSchema,
  capabilities: { keepRatio: true, rotatable: false },
  component: AudioView,
});
